// External Imports
import {
  Header as DesignSystemHeader,
  Layout,
  pxToRem,
  Stack,
  styled,
  Text,
} from '@lessonnine/design-system.lib';
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';
import PropTypes from 'prop-types';
import React from 'react';

import { breakpoints } from '../../config/css/breakpoints.mjs';
// Internal Imports
import { articleContentMaxWidth } from '../../config/css/ui.mjs';
import { LANGUAGES } from '../../config/types/prop.mjs';
import { formatDate } from '../../utils/dateUtils.mjs';
import { setInnerHtml } from '../../utils/setInnerHtml.jsx';
import { I18n } from '../I18n/I18n.jsx';
import { DsLink } from '../Link/DsLink.jsx';

// Local Variables
const AuthorName = styled(Text)`
  line-height: 1.5;
`;

const AuthorNameLink = styled(DsLink)`
  text-align: inherit;
`;

const Byline = styled(Layout)`
  font-family: ${tokens.fonts.default.name}, sans-serif;
  font-size: ${pxToRem(12)};
  margin-bottom: ${pxToRem(24)};

  @media ${breakpoints.tabletPortrait.max} {
    margin-bottom: ${pxToRem(12)};
  }
`;

const Excerpt = styled(Text)`
  font-size: ${pxToRem(18)};
  line-height: 1.5;

  @media ${breakpoints.mobile.max} {
    font-size: ${pxToRem(14)};
    line-height: 1.7143;
  }
`;

const H1WithLineHeight = styled(DesignSystemHeader.h1)`
  line-height: 1.1667;

  @media ${breakpoints.mobile.max} {
    font-size: ${pxToRem(28)};
    line-height: 1.1429;
  }
`;

const Header = styled.header`
  margin: 0 auto;
  max-width: ${pxToRem(articleContentMaxWidth)};
  text-align: center;
`;

const Separator = styled.div`
  border-left: 1px solid ${({ theme }) => theme.color.surface.layer.separator};
  display: none;
  height: ${pxToRem(22)};
  margin: 0 ${pxToRem(8)};

  @media (min-width: ${tokens.viewports.breakpoints.xxsmall}) {
    display: inline-block;
  }
`;

// Component Definition
function ArticleHeader({
  articleDate,
  authorLink,
  authorName,
  currentEdition,
  excerpt,
  isInProductMagazine,
  title,
}) {
  return (
    <Header>
      <Stack gap={16}>
        <H1WithLineHeight dangerouslySetInnerHTML={setInnerHtml(title)} />
        <Stack padding={[24, 0]}>
          <Excerpt dangerouslySetInnerHTML={setInnerHtml(excerpt)} />
        </Stack>
      </Stack>
      {authorName && (
        <Byline
          align="center"
          alignItems="center"
          direction={{ xxsmall: 'inline', xxxsmall: 'stack' }}
          gap={{ xxsmall: 0, xxxsmall: 8 }}
        >
          {}
          <AuthorName fontSize={{ xxsmall: 'base', xxxsmall: 'small' }}>
            <I18n defaultText="by" field="by" />
            &nbsp;
            {isInProductMagazine ? (
              authorName
            ) : (
              <AuthorNameLink inherited to={authorLink} underlined={false}>
                {authorName}
              </AuthorNameLink>
            )}
          </AuthorName>
          <Separator />
          {}
          <Text fontSize={{ xxsmall: 'base', xxxsmall: 'small' }}>
            {formatDate(articleDate, currentEdition)}
          </Text>
        </Byline>
      )}
    </Header>
  );
}

ArticleHeader.propTypes = {
  articleDate: PropTypes.string.isRequired,
  authorLink: PropTypes.string.isRequired,
  authorName: PropTypes.string.isRequired,
  currentEdition: LANGUAGES,
  excerpt: PropTypes.string.isRequired,
  isInProductMagazine: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
};

ArticleHeader.defaultProps = {
  currentEdition: 'en',
};

// Module Exports
export { ArticleHeader };
